import { Box, Typography, Link } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useL10n } from '@uplift/hooks';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert } from '../../../Alert';
import { InsuranceSection } from '../Settings/types';
import { useInsuranceCompaniesQuery, useUserDisplayNameQuery, useUserProfileQuery, useUserQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { ROUTING_CONFIG } from '@uplift/constants';
import { ProviderMedicalRole } from '@uplift/types';


export type UnsupportedInsuranceAlertType = {
  unsupportedMatches: { providerId: string }[];
  currentInsuranceId: string;
  insuranceSection: InsuranceSection;
  patientId: string;
};

export const UnsupportedInsuranceAlert: React.VFC<UnsupportedInsuranceAlertType> = ({
  unsupportedMatches,
  currentInsuranceId,
  insuranceSection,
  patientId
}) => {
  const { data: insuranceCompanies } = useInsuranceCompaniesQuery();
  const l10n = useL10n();
  const { classes } = useStyles();
  const history = useHistory();
  const currentInsuranceCompany = insuranceCompanies?.find(({ id }) => id === currentInsuranceId);
  const provider = unsupportedMatches[0];
  const unsupportedType = (unsupportedMatches.length === 1) ? 'single' : 'team';
  const me = useCurrentuser();
  const { data: patient } = useUserQuery(patientId);
  const { data: profile } = useUserProfileQuery(patientId);
  const { data: providerData } = useUserQuery(provider.providerId);
  const { data: providerDisplayName } = useUserDisplayNameQuery(provider.providerId);
  const providerDefaultDisplayName = providerDisplayName?.defaultDisplay ?? '';
  const psychiatryCashAmount = '$175–$250';
  const therapyCashAmount = '$125–$145';

  const title = useMemo(() => {
    switch (unsupportedType) {
      case 'single':
        if (me?.role === 'superadmin') {
          return l10n.accountSettings.insurance.adminProviderUnsupportedInsurance(providerDefaultDisplayName, currentInsuranceCompany?.name ?? '');
        }
        return l10n.accountSettings.insurance.providerUnsupportedInsurance(providerDefaultDisplayName, currentInsuranceCompany?.name ?? '');
      case 'team':
        if (me?.role === 'superadmin') {
          return l10n.accountSettings.insurance.adminCareTeamUnsupportedInsuranceTitle((profile?.preferredName || patient?.firstName) ?? '', currentInsuranceCompany?.name ?? '');
        }
        return l10n.accountSettings.insurance.careTeamUnsupportedInsurance(currentInsuranceCompany?.name ?? '');
      default:
        return;
    }
  }, [ currentInsuranceCompany?.name, l10n.accountSettings.insurance, me?.role, patient?.firstName, profile?.preferredName, providerDefaultDisplayName, unsupportedType ]);

  const cashPayAmount = useMemo(() => {
    if (unsupportedType === 'single' && providerData?.medicalRole === ProviderMedicalRole.psychiatrist) {
      return psychiatryCashAmount;
    }
    return therapyCashAmount;
  }, [ providerData?.medicalRole, unsupportedType ]);


  const desc = useMemo(() => {
    if (insuranceSection === InsuranceSection.primaryInsurance) {
      const descObj = l10n.accountSettings.insurance.unsupportedPrimaryInsurance(cashPayAmount);
      const petitionForContinuityLink = (
        <Link
          onClick={() => history.push('/support')}
          className={classes.link}
        >
          {descObj.petitionForContinuity}
        </Link>
      );
      const rematchLink = (
        <Link
          onClick={() => history.push(ROUTING_CONFIG.matching)}
          className={classes.link}
        >
          {descObj.matchWithNewTherapist}
        </Link>
      );

      return <>{descObj[0]}{petitionForContinuityLink}{descObj[1]}{rematchLink}{descObj[2]}</>;
    }

    return l10n.accountSettings.insurance.unsupportedSecondaryInsurance;
  }, [ classes.link, history, insuranceSection, l10n.accountSettings.insurance, cashPayAmount ]);


  const body = useMemo(() => {
    if (me?.role !== 'superadmin') {
      return desc;
    }


    const firstName = (profile?.preferredName || patient?.firstName) ?? '';


    if (insuranceSection === InsuranceSection.primaryInsurance) {
      if (unsupportedType === 'single') {
        return l10n.accountSettings.insurance.adminUnsupportedPrimaryInsurance(firstName, providerDefaultDisplayName);
      } else if (unsupportedType === 'team') {
        return l10n.accountSettings.insurance.adminCareTeamUnsupportedInsuranceBody(firstName);
      }
    }


    if (insuranceSection === InsuranceSection.secondaryInsurance) {
      return l10n.accountSettings.insurance.adminUnsupportedSecondaryInsurance(firstName);
    }


    return desc;
  }, [ desc, insuranceSection, l10n.accountSettings.insurance, me?.role, patient?.firstName, profile?.preferredName, providerDefaultDisplayName, unsupportedType ]);


  const content = (
    <Typography component="span" color="textPrimary">
      <Typography variant="body2" component="b" fontWeight={500} color="inherit">
        {title}&nbsp;
      </Typography>
      {body}
    </Typography>
  );

  return (
    <Alert
      severity="warning"
      icon={false}
    >
      <Box mt={-0.5}>
        {content}
      </Box>
    </Alert>
  );
};

const useStyles = makeStyles()((theme) => ({
  link: {
    color: theme.palette.text.primary,
    textDecoration: 'underline',
    '&:hover': {
      cursor: 'pointer'
    }
  }
}));
